import React, { useState, useCallback, useMemo, useRef } from 'react';
import _ from 'lodash';
import useForceUpdate from 'use-force-update';
import { useSelector } from 'react-redux';
import CategorySummaryComponent from './CategorySummaryComponent';
import AddCategoryForm from './AddCategoryForm';
import Category from '../../models/Category';
import Modal from './Modal';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import Button from './Button';
import BudgetListModes from './BudgetListModes';
import TextInput from '../elements/TextInput';
import { Provider } from "react-redux";
import store from '../../redux/store/store.js';
import InlineProgressBar from "../../../../models/components/InlineProgressBar";
import { moneyFormatFromCents } from "../../utilities/moneyFormat";
import Card from "./Card";
import DragHandle from "../elements/DragHandle.jsx";
import Budget from "../../models/Budget.jsx";
import SpentAndBudgetedSummary from "./SpentAndBudgetedSummary.jsx";
import DraggableCategory from "./DraggableCategory.jsx";
import getSelectedDateKey from "../../utilities/getSelectedDateKey.js";
import ExpandableCard from "react/components/card/ExpandableCard";

export default function CategoryGroupComponent(props) {
	const {
		index,
		showCategoryHeader,
		dragHandleProps,
		categorySubmit,
		categoryToNewTransaction,
		categoryType,
		categoryGroupType,
		categoryGroup,
		selectedDate,
		selectedDates,
		editBudgetListMode,
		submittingCategories,
		submittingBudgetsForCategoryGroup,
	} = props;
	const forceUpdate = useForceUpdate();

	const { current: editCategoryGroup } = useRef(categoryGroup.clone());

	const isNewCategoryGroup = !categoryGroup.id;
	const showTotalsWithinEachCategoryFeatureActive = useSelector((state) => state.settings.showTotalsWithinEachCategoryFeatureActive);

	const [hidingBehindButtonBecauseIAmNew, updateHidingBehindButtonBecauseIAmNew] = useState(isNewCategoryGroup);
	const [editing, updateEditing] = useState(isNewCategoryGroup);
	const [deleteDialog, updateDeleteDialog] = useState(false);
	const categoryEditBudgets = useRef(new Map());
	const categoryEditCategories = useRef(new Map());
	const setCategoryAsSubgroupCategory = useSelector((state) => state.dashboard.setCategoryAsSubgroupCategory);
	const selectedDateKey = getSelectedDateKey();

	const newCategory = useRef(new Category({
		name: '',
		category_group_id: categoryGroup.id,
	}));
	const categoryGroupDeleteUndo = props.categoryGroupDeleteUndo.bind(null, categoryGroup);

	const setToEdit = setEditing.bind(this, true);
	const setToOffEdit = setEditing.bind(this, false);

	const categoryGroupDeletePopupOpen = categoryGroupDeletePopup.bind(this, true);
	const categoryGroupDeletePopupClose = categoryGroupDeletePopup.bind(this, false);

	const budgetMayHaveUpdated = useCallback(() => {
		forceUpdate();
	}, []);

	function stopHiding() {
		updateHidingBehindButtonBecauseIAmNew(false);
	}

	function categoryGroupDelete(event) {
		const { categoryGroupDelete, categoryGroup } = props;
		return categoryGroupDelete(categoryGroup, event)
			.then(() => {
				categoryGroupDeletePopupClose();
			});
	}

	function categoryGroupDeletePopup(to) {
		console.log('setting to', to);
		updateDeleteDialog(to);
	}

	function categoryGroupSubmit(event) {
		const { categoryGroupSubmit } = props;
		return categoryGroupSubmit(editCategoryGroup, event).then(() => {
			updateEditing(!editCategoryGroup.id);
		});
	}

	function setEditing(to, event) {
		event.stopPropagation();
		console.log('clicked');
		updateEditing(to);
	}

	function categoryGroupEdit(event) {
		event.preventDefault();
		editCategoryGroup.name = event.target.value;
		forceUpdate();
	}

	function getTotalSpentForGroup() {
		const { categoryGroup, selectedDate, selectedDates } = props;
		let budget = null;
		let budgetTotal = 0;
		const categories = categoryGroup.getCategories();
		categories.forEach((category) => {
			if(selectedDate) {
				budget = category.getBudget(selectedDate);
			}
			else {
				budget = category.getMultiBudget(selectedDates);
			}
			budgetTotal += budget.getSpent();
		});
		return budgetTotal;
	}

	function getTotalBudgetedForGroup() {
		const { categoryGroup, selectedDate, selectedDates } = props;
		let budget = null;
		let budgetTotal = 0;
		const categories = categoryGroup.getCategories();
		categories.forEach((category) => {
			if(selectedDate) {
				budget = category.getBudget(selectedDate);
			}
			else {
				budget = category.getMultiBudget(selectedDates);
			}
			budgetTotal += budget.getBudgeted();
		});
		return budgetTotal;
	}

	function saveAllUpdatedBudgetsForCategoryGroup() {
		const { saveAllUpdatedBudgetsForCategoryGroup, categoryGroup } = props;
		saveAllUpdatedBudgetsForCategoryGroup(categoryGroup, categoryEditBudgets.current);
		for(let category of categories) {
			if(categoryEditCategories.current.get(category).hasChanges()) {
				categorySubmit(categoryEditCategories.current.get(category));
			}
		}
	}

	const categories = useMemo(() => {
		const categories = categoryGroup.getVisibleCategoryForMonths(selectedDates, editBudgetListMode === BudgetListModes.DASHBOARD);


		for(let category of categories) {
			let budget = null;
			if(_.size(selectedDates) > 1) {
				budget = category.getMultiBudget(selectedDates);
			}
			else {
				budget = category.getBudget(selectedDate).clone();
				budget.update({
					interest_amount: budget.interest_amount === null ? '' : budget.interest_amount,
					minimum_payment: budget.minimum_payment === null ? '' : budget.minimum_payment,
					fund_total: budget.fund_total === null ? '' : budget.fund_total,
				});
			}
			categoryEditBudgets.current.set(category, budget);
			// if(!categoryEditCategories.current.has(category)) {
			categoryEditCategories.current.set(category, category.clone());
			// }
		}
		// console.log('category group changed, so update categories!');
		return categories;
	}, [selectedDates, categoryGroup, editBudgetListMode, categoryGroup.categories]);

	function aBudgetHasChanges() {
		for(let category of categories) {
			if(categoryEditBudgets.current.get(category).hasChanges()) {
				return true;
			}
			if(categoryEditCategories.current.get(category).hasChanges()) {
				return true;
			}
		}
		return false;
	}

	const saveBudgetChangesButton = () => {
		if(!categoryGroup.id) {
			return null;
		}
		return null;
		return (
			<React.Fragment>
				{editBudgetListMode === BudgetListModes.AMOUNTS && _.size(selectedDates) === 1 &&
				<div className='row'>
					<Button
						block
						disabled={!aBudgetHasChanges(selectedDate) || submittingBudgetsForCategoryGroup[categoryGroup.id] || !_.isEmpty(submittingCategories)}
						onClick={saveAllUpdatedBudgetsForCategoryGroup}
					>
						Save Budget Changes
					</Button>
				</div>
				}
			</React.Fragment>
		);
	};

	if(hidingBehindButtonBecauseIAmNew) {
		return (
			<Card
				className='category_group new_category_group darken-1'>
				<Button
					flat
					bright
					onClick={stopHiding}
				>
					Add New Group +
				</Button>
			</Card>
		);
	}

	function getCategoryCardHeading() {
		return <React.Fragment>
			{categoryGroup.id &&
			<div className="row">
				<h5 className="heading">
					<span
						onClick={setToEdit}
						className="clickable">
						{categoryGroup.name}
					</span>
					<DragHandle
						className={`${editBudgetListMode === BudgetListModes.TYPES ? "" : "hide"}`}
						{...dragHandleProps}
					/>
				</h5>
			</div>
			}
		</React.Fragment>;
	}

	return (
		<ExpandableCard
			className='category_group darken-1'
			toggleableOnlyOnChevron
			cardTitle={getCategoryCardHeading()}
			cardAction={showTotalsWithinEachCategoryFeatureActive &&
			editBudgetListMode === BudgetListModes.DASHBOARD &&
			categories.length > 1 &&
			<div
				className='category_summary footer_items'>
				<SpentAndBudgetedSummary
					budgetedWord={categoryGroup.type === Category.types.INCOME ? 'Planned' : 'Budgeted'}
					spentWord={categoryGroup.type === Category.types.INCOME ? 'Received' : 'Spent'}
					spent={getTotalSpentForGroup()}
					budgeted={getTotalBudgetedForGroup()}
				/>
			</div>
			}
		>
			{deleteDialog &&
			<Modal
				footer={(
					<React.Fragment>
						<Button
							danger
							onClick={categoryGroupDelete}
						>Delete Category!
						</Button>
						{' '}
						<Button
							onClick={categoryGroupDeletePopupClose}
						>Cancel
						</Button>
					</React.Fragment>
				)}
				onClose={categoryGroupDeletePopupClose}
			>
				<div>Remove the Category Group {categoryGroup.name}?</div>
			</Modal>
			}
			{editing &&
			<form onSubmit={categoryGroupSubmit} autoComplete="off">
				<div className="row">
					<div className="input-field col s12 m6 l6">
						<TextInput
							type="text"
							label={categoryGroup.id ? `Change ${categoryGroup.name}'s Name` : `Add New Budget Group within ${categoryGroupType.name}`}
							name="category_group_name"
							onChange={categoryGroupEdit}
							value={editCategoryGroup.name}
						/>
					</div>
				</div>
				<div className="row">
					<div className=" col s12">
						<Button
							type="submit"
						>
							Submit
						</Button>
						{" "}


						{!!categoryGroup.id &&
						<React.Fragment>
							<Button
								danger
								onClick={setToOffEdit}
								type="button"
							>
								Cancel
							</Button>
							{" "}
						</React.Fragment>
						}
						{categoryGroup.id &&
						<React.Fragment>
							{!categoryGroup.deleted &&
							<Button
								danger
								className="right"
								onClick={categoryGroupDeletePopupOpen}>
								Delete
							</Button>
							}
							{categoryGroup.deleted &&
							<Button className="right" onClick={categoryGroupDeleteUndo}>Undo</Button>
							}
							{" "}
						</React.Fragment>
						}
					</div>
				</div>
			</form>
			}
			{categories.length >= 3 && saveBudgetChangesButton()}
			{categories.length > 0 &&
			<React.Fragment>
				<div>
					{showCategoryHeader && editBudgetListMode === BudgetListModes.DASHBOARD &&
					<div className='category_summary heading_items'>
						<div className='section_items row'>
							<span className='col s6'>Name</span>
							<span
								className='col s3'>
								{categoryGroup.type === Category.types.INCOME ? 'Planned' : 'Budgeted'}
							</span>
							{categoryGroup.type === Category.types.EXPENSE &&
							<span className='col s3'>Remaining</span>
							}
							{categoryGroup.type === Category.types.INCOME &&
							<span className='col s3'>Received</span>
							}
							{categoryGroup.type === Category.types.LOANS &&
							<span className='col s3'>Remaining</span>
							}
							{categoryGroup.type === Category.types.SAVINGS &&
							<span className='col s3'>Saved</span>
							}
						</div>
					</div>
					}
					<div>
						<Droppable
							droppableId={`categoryGroup-${categoryGroup.id}`}
							type='CATEGORY'>
							{(provided, snapshot) => (
								<div
									ref={provided.innerRef}
									{...provided.droppableProps}
								>
									{categories.map((category, index) => (
										<DraggableCategory
											key={`${category.id} ${selectedDateKey}`}
											{...props}
											selectedDateKey={selectedDateKey}
											categoryEditCategories={categoryEditCategories}
											categoryToNewTransaction={categoryToNewTransaction}
											categoryEditBudgets={categoryEditBudgets}
											budgetMayHaveUpdated={budgetMayHaveUpdated}
											index={index}
											category={category}
										/>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</div>
				</div>
				{saveBudgetChangesButton()}
			</React.Fragment>
			}
			{!!categoryGroup.id && selectedDate && editBudgetListMode === BudgetListModes.TYPES && !setCategoryAsSubgroupCategory &&
			<Provider store={store}>
				<AddCategoryForm
					{...props}
					category={newCategory.current}
					type={categoryType}
					categorySubmit={categorySubmit}
				/>
			</Provider>
			}

		</ExpandableCard>
	);
}